// Web Push subscription helper.
// Subscribes the browser to the backend's VAPID-signed push channel and
// keeps the subscription in sync.

import api from "@/lib/api";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermission() {
  return pushSupported() ? Notification.permission : "denied";
}

export async function ensureSubscribed() {
  if (!pushSupported()) throw new Error("Push not supported on this device");
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get("/push/public-key");
    if (!data?.public_key) throw new Error("Server is missing VAPID key");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent,
  });
  return sub;
}

export async function unsubscribe() {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch { /* ignore */ }
  await sub.unsubscribe();
}

export async function requestAndSubscribe() {
  if (!pushSupported()) throw new Error("Push not supported");
  let perm = Notification.permission;
  if (perm === "default") perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications blocked");
  return ensureSubscribed();
}

export async function sendTest() {
  await api.post("/push/test");
}
